import { phishingSignsLabels } from '@/client/campaigns';
import { MyPhishingSimulationDetail } from '@/client/my-phishing';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import {
  useMarkSimulationViewed,
  useMyPhishingSimulationDetail,
} from '@/hooks/query';
import { usePhishingFollowUpEnabled } from '@/hooks/usePhishingFollowUpEnabled';
import CampaignThumbnailPlaceholder from '@/images/placeholders/campaign-thumbnail-placeholder.svg';
import { LoadingPage } from '@/pages/LoadingPage';
import { AppAccordion } from '@/ui/accordion/AppAccordion';
import { FormatDate } from '@/ui/date';
import { stripUnwantedHtmlTags } from '@/utils/helpers';
import { AccordionTab } from 'primereact/accordion';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { LEARNER_CONTENT_FORBIDDEN_TAGS } from './constants';
import { SimulationDetailBanner } from './SimulationDetailBanner';
import { SimulationDetailNavigation } from './SimulationDetailNavigation';
import { SimulationDetailStepper } from './SimulationDetailStepper';

const TOTAL_STEPS = 4;

const StyledDetailContainer = styled.div`
  width: 100%;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  background-color: var(--beige-main);
  border: 1px solid var(--gray-dark);
  border-radius: var(--xsmall-border-radius);
  padding: 50px;
  box-sizing: border-box;
  gap: 25px;

  @media screen and (max-width: 860px) {
    padding: var(--medium-padding);
    gap: var(--medium-padding);
  }
`;

const BackArrowIcon = () => (
  <svg
    width="15"
    height="11"
    viewBox="0 0 14.8 10.6"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ transform: 'rotate(180deg)' }}
  >
    <path
      d="M9.5 10.6L8.8 9.9L12.875 5.8H0V4.8H12.875L8.8 0.7L9.5 0L14.8 5.3L9.5 10.6Z"
      fill="currentColor"
    />
  </svg>
);

const EmailPreviewIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M17.5 5C18.163 5 18.7989 5.26339 19.2678 5.73223C19.7366 6.20107 20 6.83696 20 7.5V15.5C20 16.163 19.7366 16.7989 19.2678 17.2678C18.7989 17.7366 18.163 18 17.5 18H6.5C5.83696 18 5.20107 17.7366 4.73223 17.2678C4.26339 16.7989 4 16.163 4 15.5V7.5C4 6.83696 4.26339 6.20107 4.73223 5.73223C5.20107 5.26339 5.83696 5 6.5 5H17.5ZM19 8.961L12.254 12.931C12.1902 12.9685 12.1189 12.9913 12.0453 12.9979C11.9716 13.0044 11.8974 12.9946 11.828 12.969L11.746 12.931L5 8.963V15.5C5 15.8978 5.15804 16.2794 5.43934 16.5607C5.72064 16.842 6.10218 17 6.5 17H17.5C17.8978 17 18.2794 16.842 18.5607 16.5607C18.842 16.2794 19 15.8978 19 15.5V8.961ZM17.5 6H6.5C6.10218 6 5.72064 6.15804 5.43934 6.43934C5.15804 6.72064 5 7.10218 5 7.5V7.802L12 11.92L19 7.8V7.5C19 7.10218 18.842 6.72064 18.5607 6.43934C18.2794 6.15804 17.8978 6 17.5 6Z"
      fill="currentColor"
    />
  </svg>
);

const LandingPageIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4.625 19C4.15833 19 3.771 18.846 3.463 18.538C3.15433 18.2293 3 17.8417 3 17.375V6.625C3 6.15833 3.15433 5.771 3.463 5.463C3.771 5.15433 4.15833 5 4.625 5H19.375C19.8417 5 20.2293 5.15433 20.538 5.463C20.846 5.771 21 6.15833 21 6.625V17.375C21 17.8417 20.846 18.2293 20.538 18.538C20.2293 18.846 19.8417 19 19.375 19H4.625ZM4.625 18H19.375C19.5417 18 19.6873 17.9373 19.812 17.812C19.9373 17.6873 20 17.5417 20 17.375V8H4V17.375C4 17.5417 4.06267 17.6873 4.188 17.812C4.31267 17.9373 4.45833 18 4.625 18Z"
      fill="currentColor"
    />
  </svg>
);

const RedirectPageIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M8.11538 17.5C7.65513 17.5 7.27083 17.3458 6.9625 17.0375C6.65417 16.7292 6.5 16.3449 6.5 15.8846V14.3846H7.5V15.8846C7.5 16.0385 7.5641 16.1795 7.6923 16.3077C7.82052 16.4359 7.96154 16.5 8.11538 16.5H18.8846C19.0385 16.5 19.1795 16.4359 19.3077 16.3077C19.4359 16.1795 19.5 16.0385 19.5 15.8846V6.5H7.5V8.61538H6.5V5.11537C6.5 4.65512 6.65417 4.27083 6.9625 3.9625C7.27083 3.65417 7.65513 3.5 8.11538 3.5H18.8846C19.3449 3.5 19.7292 3.65417 20.0375 3.9625C20.3458 4.27083 20.5 4.65512 20.5 5.11537V15.8846C20.5 16.3449 20.3458 16.7292 20.0375 17.0375C19.7292 17.3458 19.3449 17.5 18.8846 17.5H8.11538ZM5.11538 20.5C4.65513 20.5 4.27083 20.3458 3.9625 20.0375C3.65417 19.7292 3.5 19.3449 3.5 18.8846V7.11537H4.5V18.8846C4.5 19.0385 4.5641 19.1795 4.6923 19.3077C4.8205 19.4359 4.96153 19.5 5.11538 19.5H16.8846V20.5H5.11538ZM12.4231 15.1731L11.7346 14.4846L14.175 12H6.5V11H14.175L11.7346 8.51537L12.4231 7.82693L16.0962 11.5L12.4231 15.1731Z"
      fill="currentColor"
    />
  </svg>
);

const StyledBackLink = styled.button`
  all: unset;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: var(--xsmall-padding);
  font-size: var(--small-font-size);
  line-height: 1.4;
  color: var(--black-main);

  &:hover {
    color: var(--red-dark);
  }

  &:focus-visible {
    outline: 2px solid var(--red-dark);
    outline-offset: 2px;
  }
`;

const StyledContentFrame = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

/* Step 1 */
const StyledSimulationCard = styled.div`
  background-color: var(--white-main);
  border: 1px solid var(--gray-moderate);
  border-radius: 20px 20px 0 0;
  padding: 30px;
  display: flex;
  flex-direction: column;
  gap: var(--default-padding-small);
`;

const StyledTwoColumnLayout = styled.div`
  display: flex;
  gap: 30px;

  @media screen and (max-width: 860px) {
    flex-direction: column;
  }
`;

const StyledLeftColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 26px;
  min-width: 0;
`;

const StyledRightColumn = styled.div`
  flex: 1;
  min-width: 0;

  @media screen and (max-width: 860px) {
    order: -1;
  }
`;

const StyledRightImage = styled.img`
  width: 100%;
  height: auto;
  object-fit: contain;
  border-radius: var(--xsmall-border-radius);
`;

const StyledAboutSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const StyledSectionHeading = styled.h2`
  margin: 0;
  font-weight: 600;
  font-size: var(--medium-font-size);
  line-height: 24px;
  color: var(--black-main);
`;

const StyledDescriptionBody = styled.div`
  font-weight: 400;
  font-size: var(--medium-font-size);
  line-height: 24px;
  color: var(--black-main);

  h3 {
    font-weight: 600;
    font-size: var(--medium-font-size);
    line-height: 24px;
    margin: 16px 0 8px;

    &:first-child {
      margin-top: 0;
    }
  }

  p {
    margin: 0 0 12px;
  }

  ul {
    margin: 8px 0;
    padding-left: 20px;
  }

  li {
    margin-bottom: 4px;
  }
`;

const StyledCharacteristicsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const StyledTagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const StyledCharacteristicTag = styled.span`
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 2px 12px;
  border-radius: 100px;
  background-color: rgba(14, 151, 151, 0.2);
  color: var(--green-color);
  font-weight: 500;
  font-size: var(--xsmall-font-size);
  line-height: var(--xsmall-line-height);
  white-space: nowrap;
`;

const StyledLearningObjectiveAccordion = styled.div`
  .p-accordion {
    .p-accordion-tab {
      background-color: transparent;
      border: 1px solid var(--gray-moderate);
      border-radius: var(--box-border-radius);
      padding: 30px;

      .p-accordion-header {
        background-color: transparent;

        &:not(.p-disabled):hover {
          background-color: transparent;
        }

        .p-accordion-header-link {
          background-color: transparent;
          padding: 0;
          font-weight: 600;
          font-size: var(--medium-font-size);
          line-height: 24px;

          &:not(.p-disabled):hover {
            background-color: transparent;
          }

          &:focus {
            box-shadow: none;
          }
        }

        &:not(.p-disabled).p-highlight .p-accordion-header-link {
          background-color: transparent;
        }

        &:not(.p-disabled).p-highlight:hover .p-accordion-header-link {
          background-color: transparent;
        }
      }

      .p-accordion-content {
        padding: 26px 0 0;
        font-size: var(--medium-font-size);
        line-height: 24px;

        p,
        h1,
        h2,
        h3 {
          margin: 0;
          padding: 0;
        }

        strong {
          font-weight: 600;
        }
      }
    }
  }
`;

/* Steps 2-4 */
const StyledTitleBar = styled.div`
  background-color: var(--beige-main);
  border: 1px solid var(--gray-dark);
  border-bottom: none;
  border-radius: var(--xsmall-border-radius) var(--xsmall-border-radius) 0 0;
  padding: 15px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
  font-size: var(--small-font-size);
  line-height: var(--small-line-height);
  color: var(--black-main);
`;

const StyledMetaBar = styled.div`
  background-color: var(--beige-main);
  border: 1px solid var(--gray-dark);
  padding: 15px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: var(--small-font-size);
  line-height: var(--small-line-height);
`;

const StyledMetaRow = styled.div`
  display: flex;
  gap: 8px;

  @media screen and (max-width: 480px) {
    flex-direction: column;
    gap: 2px;
  }
`;

const StyledMetaLabel = styled.span`
  font-weight: 500;
  color: var(--gray-darker);
  min-width: 60px;
`;

const StyledMetaValue = styled.span`
  font-weight: 500;
  color: var(--black-main);
`;

const StyledPreviewArea = styled.div`
  background-color: var(--white-main);
  border-left: 1px solid var(--gray-moderate);
  border-right: 1px solid var(--gray-moderate);
  padding: 100px;
  min-height: 500px;
  flex: 1;

  @media screen and (max-width: 860px) {
    padding: var(--default-padding);
    min-height: 300px;
  }
`;

const PREVIEW_READONLY_STYLES =
  '<style>a,button,input,select,textarea,form{pointer-events:none!important;cursor:default!important}a{color:inherit!important;text-decoration:inherit!important}</style>';

const readOnlySrcDoc = (
  html: string | null | undefined,
): string | undefined => {
  if (!html) return undefined;
  if (/<head[^>]*>/i.test(html)) {
    return html.replace(/<head[^>]*>/i, `$&${PREVIEW_READONLY_STYLES}`);
  }
  return `${PREVIEW_READONLY_STYLES}${html}`;
};

const StyledHtmlPreviewIframe = styled.iframe.attrs({ tabIndex: -1 })`
  width: 100%;
  min-height: 600px;
  border: none;
  display: block;
`;

const WarningSignsStep: React.FC<{
  detail: MyPhishingSimulationDetail;
  phishingSignLabel: string | undefined;
}> = ({ detail, phishingSignLabel }) => {
  const { t } = useTranslation();
  const [objectiveExpanded, setObjectiveExpanded] = useState<number | null>(
    null,
  );

  return (
    <StyledSimulationCard>
      <StyledTwoColumnLayout>
        <StyledLeftColumn>
          <StyledAboutSection>
            <StyledSectionHeading>
              {t('phishing.followUp.detail.warningSignsAbout')}
            </StyledSectionHeading>
            <StyledDescriptionBody
              dangerouslySetInnerHTML={{
                __html: stripUnwantedHtmlTags(
                  detail.steps.explanation.description,
                  LEARNER_CONTENT_FORBIDDEN_TAGS,
                ),
              }}
            />
          </StyledAboutSection>

          {detail.steps.explanation.characteristics.length > 0 && (
            <StyledCharacteristicsSection>
              <StyledSectionHeading>
                {t('phishing.followUp.detail.warningSignsCharacteristics')}
              </StyledSectionHeading>
              <StyledTagsContainer>
                {detail.steps.explanation.characteristics.map(
                  (characteristic) => (
                    <StyledCharacteristicTag key={characteristic.name}>
                      {characteristic.name}
                    </StyledCharacteristicTag>
                  ),
                )}
              </StyledTagsContainer>
            </StyledCharacteristicsSection>
          )}

          {phishingSignLabel && detail.phishingSignContent && (
            <StyledLearningObjectiveAccordion>
              <AppAccordion
                activeIndex={objectiveExpanded}
                onTabChange={(e) => {
                  const idx = typeof e.index === 'number' ? e.index : null;
                  setObjectiveExpanded(idx === objectiveExpanded ? null : idx);
                }}
              >
                <AccordionTab
                  header={
                    objectiveExpanded === 0
                      ? `${t(
                          'phishing.followUp.detail.learningObjective',
                        )}: ${phishingSignLabel}`
                      : t('phishing.followUp.detail.warningSignsReadMore', {
                          sign: phishingSignLabel.toLowerCase(),
                        })
                  }
                >
                  <div
                    dangerouslySetInnerHTML={{
                      __html: stripUnwantedHtmlTags(
                        detail.phishingSignContent,
                        LEARNER_CONTENT_FORBIDDEN_TAGS,
                      ),
                    }}
                  />
                </AccordionTab>
              </AppAccordion>
            </StyledLearningObjectiveAccordion>
          )}
        </StyledLeftColumn>

        <StyledRightColumn>
          <StyledRightImage
            src={detail.thumbnailUrl ?? CampaignThumbnailPlaceholder}
            alt={detail.name}
          />
        </StyledRightColumn>
      </StyledTwoColumnLayout>
    </StyledSimulationCard>
  );
};

export const LearnerPhishingDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { campaignId } = useParams<{ campaignId: string }>();
  const [activeStep, setActiveStep] = useState(0);
  const { enabled: featureEnabled, account } = usePhishingFollowUpEnabled();

  useEffect(() => {
    if (account && !featureEnabled) {
      navigate(RedirectPaths[RedirectPathsEnum.LEARNER_DASHBOARD_ACTIVE](), {
        replace: true,
      });
    }
  }, [account, featureEnabled, navigate]);

  const { detail, isLoading } = useMyPhishingSimulationDetail(campaignId ?? '');
  const { mutateAsync: markViewed } = useMarkSimulationViewed();

  const stepLabels = [
    t('phishing.followUp.detail.step.warningSignsLabel'),
    t('phishing.followUp.detail.step.emailPreviewLabel'),
    t('phishing.followUp.detail.step.landingPageLabel'),
    t('phishing.followUp.detail.step.redirectPageLabel'),
  ];

  const labels = phishingSignsLabels(t);

  const handleBack = useCallback(() => {
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
    }
  }, [activeStep]);

  const handleNext = useCallback(() => {
    if (activeStep < TOTAL_STEPS - 1) {
      setActiveStep((prev) => prev + 1);
    }
  }, [activeStep]);

  const handleFinish = useCallback(async () => {
    if (campaignId) {
      await markViewed(campaignId);
    }
    navigate(RedirectPaths[RedirectPathsEnum.LEARNER_PHISHING]());
  }, [campaignId, markViewed, navigate]);

  const handleBackToOverview = useCallback(() => {
    navigate(RedirectPaths[RedirectPathsEnum.LEARNER_PHISHING]());
  }, [navigate]);

  if (isLoading || !detail) {
    return <LoadingPage message={t('generic.loading')} />;
  }

  const phishingSignLabel = detail.phishingSign
    ? labels[detail.phishingSign as keyof typeof labels]
    : undefined;

  const navigation = (
    <SimulationDetailNavigation
      activeStep={activeStep}
      totalSteps={TOTAL_STEPS}
      onBack={handleBack}
      onNext={handleNext}
      onFinish={handleFinish}
    />
  );

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <>
            <WarningSignsStep
              detail={detail}
              phishingSignLabel={phishingSignLabel}
            />
            {navigation}
          </>
        );
      case 1:
        return (
          <>
            <StyledTitleBar>
              <EmailPreviewIcon />
              {t('phishing.followUp.detail.emailPreview.title')}
            </StyledTitleBar>
            <StyledMetaBar>
              <StyledMetaRow>
                <StyledMetaLabel>
                  {t('phishing.followUp.detail.emailPreview.from')}:
                </StyledMetaLabel>
                <StyledMetaValue>
                  {detail.steps.emailPreview.senderDisplayName} &lt;
                  {detail.steps.emailPreview.senderEmail}&gt;
                </StyledMetaValue>
              </StyledMetaRow>
              <StyledMetaRow>
                <StyledMetaLabel>
                  {t('phishing.followUp.detail.emailPreview.subject')}:
                </StyledMetaLabel>
                <StyledMetaValue>
                  {detail.steps.emailPreview.subject}
                </StyledMetaValue>
              </StyledMetaRow>
              <StyledMetaRow>
                <StyledMetaLabel>
                  {t('phishing.followUp.detail.emailPreview.date')}:
                </StyledMetaLabel>
                <StyledMetaValue>
                  <FormatDate date={detail.receivedAt} format="D MMM YYYY" />
                </StyledMetaValue>
              </StyledMetaRow>
            </StyledMetaBar>
            <StyledPreviewArea>
              {detail.steps.emailPreview.html && (
                <StyledHtmlPreviewIframe
                  srcDoc={readOnlySrcDoc(detail.steps.emailPreview.html)}
                  sandbox="allow-same-origin"
                  title={t('phishing.followUp.detail.emailPreview.title')}
                />
              )}
            </StyledPreviewArea>
            {navigation}
          </>
        );
      case 2:
        return (
          <>
            <StyledTitleBar>
              <LandingPageIcon />
              {t('phishing.followUp.detail.landingPage.title')}
            </StyledTitleBar>
            <StyledPreviewArea>
              {detail.steps.landingPage.html && (
                <StyledHtmlPreviewIframe
                  srcDoc={readOnlySrcDoc(detail.steps.landingPage.html)}
                  sandbox="allow-same-origin"
                  title={t('phishing.followUp.detail.landingPage.title')}
                />
              )}
            </StyledPreviewArea>
            {navigation}
          </>
        );
      case 3:
        return (
          <>
            <StyledTitleBar>
              <RedirectPageIcon />
              {t('phishing.followUp.detail.redirectPage.title')}
            </StyledTitleBar>
            <StyledPreviewArea>
              {detail.steps.redirectPage.html && (
                <StyledHtmlPreviewIframe
                  srcDoc={readOnlySrcDoc(detail.steps.redirectPage.html)}
                  sandbox="allow-same-origin"
                  title={t('phishing.followUp.detail.redirectPage.title')}
                />
              )}
            </StyledPreviewArea>
            {navigation}
          </>
        );
      default:
        return null;
    }
  };

  return (
    <StyledDetailContainer>
      <StyledBackLink onClick={handleBackToOverview}>
        <BackArrowIcon /> {t('phishing.followUp.detail.backToOverview')}
      </StyledBackLink>

      <SimulationDetailBanner detail={detail} />

      <SimulationDetailStepper
        stepLabels={stepLabels}
        activeStep={activeStep}
        onStepClick={setActiveStep}
      />

      <StyledContentFrame>{renderStepContent()}</StyledContentFrame>
    </StyledDetailContainer>
  );
};
